import { useCallback, useMemo, useState } from 'react';
import {
  Participant,
  ParticipantField,
  ParticipantFieldCategory,
  useParticipantFields,
  useParticipantFieldsByCategory,
} from '@app/api';

export interface ProfileCategory {
  category: ParticipantFieldCategory;
  fieldNames: string[];
  fields: ParticipantField[];
}

interface ProfileStep {
  steps: string[];
  currentStep: string;
  currentFieldsByCategories: ProfileCategory[];
  onSubmit: (participant: Partial<Participant>) => void;
}

interface Params {
  criteriaRefs: string[];
  onFinish: (participant: Partial<Participant>) => any;
}

export const useDisplayedFieldsForCriteria = (criteriaRefs: string[] = []) => {
  const { data: fields = [] } = useParticipantFields();

  return useMemo(
    () => fields.filter((field) => criteriaRefs.includes(field.name)),
    [criteriaRefs, fields],
  );
};

export const useProfileSteps = ({
  criteriaRefs = [],
  onFinish,
}: Params): ProfileStep => {
  const [participant, setParticipant] = useState<Partial<Participant>>({});
  const [currentStep, setCurrentStep] = useState(0);

  const fields = useParticipantFieldsByCategory();

  const displayedFieldsByCategory = useMemo(() => {
    const fieldsByCategory = {} as Record<
      ParticipantFieldCategory,
      ParticipantField[]
    >;

    (Object.keys(fields) as ParticipantFieldCategory[]).forEach((cat) => {
      const toKeep = fields[cat].filter((f) => criteriaRefs.includes(f.name));
      if (toKeep.length) {
        fieldsByCategory[cat] = toKeep;
      }
    });
    return fieldsByCategory;
  }, [fields, criteriaRefs]);

  const steps = useMemo(() => {
    const profileCategories = Object.entries(displayedFieldsByCategory).map(
      ([category, fields]) => ({
        category: category as ParticipantFieldCategory,
        fieldNames: fields.map((field) => field.name),
        fields,
      }),
    );

    const finalSteps = [];
    let currSize = 0;
    for (const cat of profileCategories) {
      if (currSize === 0) {
        finalSteps.push([cat]);
        currSize += cat.fieldNames.length;
        continue;
      }

      const catSize = cat.fieldNames.length;
      if (currSize + catSize > 5) {
        finalSteps.push([cat]);
        currSize = catSize;
      } else {
        finalSteps[finalSteps.length - 1].push(cat);
        currSize += catSize;
      }
    }

    return finalSteps;
  }, [displayedFieldsByCategory]);

  const stepsNames = useMemo(
    () => steps.map((_, index) => `step_${index}`),
    [steps],
  );

  const onSubmit = useCallback(
    (profile: Partial<Participant>) => {
      const finalValue = { ...participant, ...profile };
      setParticipant(finalValue);
      if (currentStep >= steps.length - 1) {
        return onFinish(finalValue);
      } else {
        setCurrentStep(currentStep + 1);
      }
    },
    [participant, currentStep, steps, onFinish, setCurrentStep, setParticipant],
  );

  return {
    currentFieldsByCategories: steps[currentStep],
    steps: stepsNames,
    currentStep: `step_${currentStep}`,
    onSubmit,
  };
};
